import { Football, GoogleLogo, ArrowsLeftRight, Binoculars, ChartLineUp, Scales, Clipboard, Tray, UsersThree, Medal, ClockCounterClockwise, Lightning, Target, UsersFour, Wrench, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { trackEvent } from "@/App";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";

const featureGroups = [
  {
    title: "TRADE TOOLS",
    desc: "Everything you need to find, evaluate, and execute winning trades.",
    color: "#ccff00",
    features: [
      { icon: ArrowsLeftRight, name: "AI Trade Analyzer", desc: "Select players or picks to trade, choose a strategy (Win Now / Balanced / Rebuild), and get 6 AI-generated trade packages. Each suggestion is value-balanced within 8% and validated against real roster data.", badge: "AI-Powered", link: "/features/trade-analyzer" },
      { icon: Binoculars, name: "Trade Finder", desc: "One-click auto-scan of your entire roster against every team in the league. Algorithmically surfaces mutual-fit trades based on positional needs — zero AI cost, instant results.", badge: "Zero Cost", link: "/features/trade-finder" },
      { icon: ChartLineUp, name: "Trade Simulator", desc: "Select any trade and see the before/after impact: total value change, power ranking movement, positional grade changes. Know the impact before you propose.", badge: "Before/After", link: "/features/trade-simulator" },
      { icon: Scales, name: "Trade Calculator", desc: "Manual side-by-side value comparison for two teams. Add players and picks, see total values with waiver adjustment for uneven trades.", badge: "Manual" },
      { icon: Clipboard, name: "Trade Evaluator", desc: "Type any player names into two sides and instantly see the fairness verdict: FAIR, CLOSE, or LOPSIDED. Perfect for quickly evaluating trades proposed to you.", badge: "Quick Check" },
      { icon: Tray, name: "Trade Inbox", desc: "Save AI trade suggestions with one click, track status (pending/executed/dismissed), and share proposals via link. Your trade history in one place.", badge: "Save & Share" },
    ],
  },
  {
    title: "LEAGUE ANALYSIS",
    desc: "Understand your league landscape to target the right trade partners.",
    color: "#4ade80",
    features: [
      { icon: UsersThree, name: "All Teams", desc: "View every team&apos;s roster with player values, positional rankings (#1-#12 per position), draft capital, and player/picks toggle. Switch between total value and player-only rankings." },
      { icon: Medal, name: "Contender Rankings", desc: "Every team scored 0-100 on a Contender vs Rebuilder scale. Based on starter quality, age profile, draft capital ratio, positional completeness, and relative strength. Know who&apos;s buying and who&apos;s selling.", link: "/features/roster-tools" },
      { icon: ClockCounterClockwise, name: "League History", desc: "Trace your league back up to 5 seasons with win/loss records, point totals, and standings charts. See how teams have evolved over time." },
    ],
  },
  {
    title: "DRAFT TOOLS",
    desc: "Prepare for your rookie draft with AI-powered analysis.",
    color: "#45b7d1",
    features: [
      { icon: Lightning, name: "Draft War Room", desc: "Visual draft board showing all picks with real owners. Select any of your picks for AI advice: who to draft, trade down analysis, or trade for an established player.", link: "/features/draft-room" },
      { icon: Football, name: "Mock Draft", desc: "Simulate the entire rookie draft with a BPA-first algorithm. See projected picks for all teams based on roster needs. Your picks highlighted so you can plan strategy.", link: "/features/draft-room" },
    ],
  },
  {
    title: "PLAYER TOOLS",
    desc: "Deep player analysis and roster optimization.",
    color: "#f9ca24",
    features: [
      { icon: UsersFour, name: "Player Compare", desc: "Search and compare up to 4 players side-by-side: dynasty value, rank, tier, age, trend, trade frequency, college, and more. Visual bar chart comparison.", link: "/features/roster-tools" },
      { icon: Target, name: "Roster Optimizer", desc: "Set your ideal lineup targets (e.g., 2 QBs, 3 RBs, 4 WRs) and the AI creates a multi-step trade plan to get there. Specific players, values, and trade partners.", link: "/features/roster-tools" },
      { icon: Wrench, name: "Player Detail", desc: "Click any player to see season stats, game log bar chart, weekly stat table, injury status, depth chart position, and bio. Full Sleeper data integration." },
    ],
  },
];

export default function FeaturesPage() {
  const handleGoogleLogin = () => {
    const r = window.location.origin + '/';
    window.location.href = '/api/auth/google/login';
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO page="features" />
      <PublicNav />

      <div className="max-w-5xl mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white uppercase mb-4" data-testid="features-title">
            EVERY <span className="text-[#ccff00]">FEATURE</span>
          </h1>
          <p className="text-lg text-[#a3a3a3] max-w-2xl mx-auto">15+ tools built specifically for dynasty fantasy football managers who want a competitive edge.</p>
        </div>

        {featureGroups.map((group) => (
          <div key={group.title} className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-8 rounded-sm" style={{ backgroundColor: group.color }} />
              <div>
                <h2 className="font-heading text-2xl text-white uppercase">{group.title}</h2>
                <p className="text-xs text-[#a3a3a3]">{group.desc}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {group.features.map((f) => {
                const Icon = f.icon;
                const card = (
                  <div className={`border border-[#262626] bg-[#171717] p-5 transition-colors ${f.link ? 'hover:border-[#ccff00]/30 cursor-pointer' : ''}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Icon size={20} weight="bold" style={{ color: group.color }} />
                        <h3 className="font-heading text-lg text-white uppercase">{f.name}</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        {f.badge && <span className="text-[8px] font-bold uppercase px-2 py-0.5 rounded-sm" style={{ color: group.color, backgroundColor: `${group.color}15`, border: `1px solid ${group.color}30` }}>{f.badge}</span>}
                      </div>
                    </div>
                    <p className="text-sm text-[#a3a3a3] leading-relaxed">{f.desc}</p>
                    {f.link && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[#ccff00] font-bold uppercase tracking-wider mt-3">
                        SEE FULL DETAILS <ArrowRight size={10} weight="bold" />
                      </span>
                    )}
                  </div>
                );
                return f.link ? (
                  <Link key={f.name} to={f.link} className="no-underline">{card}</Link>
                ) : (
                  <div key={f.name}>{card}</div>
                );
              })}
            </div>
          </div>
        ))}

        <div className="text-center py-12 border-t border-[#262626]">
          <h2 className="font-heading text-3xl text-white uppercase mb-3">START FOR FREE</h2>
          <p className="text-sm text-[#a3a3a3] mb-6">30 free credits. No credit card. Import your Sleeper leagues and explore every feature.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button onClick={() => { trackEvent("try_free_click"); window.location.href = "/"; }} className="h-11 px-8 btn-primary rounded-sm text-sm">
              <ArrowRight size={16} weight="bold" className="mr-2" /> EXPLORE FREE
            </Button>
            <Button onClick={handleGoogleLogin} className="h-11 px-8 bg-white text-black font-bold text-sm rounded-sm hover:bg-gray-100">GO PRO — FROM $19.99/yr</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
